import throwMessage from "../../utils/throwMessage";

const getAuthHeaders = () => {
  const TC_TOKEN_AUTH = localStorage.getItem("TC_TOKEN_AUTH");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${TC_TOKEN_AUTH}`,
    },
    userId: user._id,
    role: user.role,
  };
};

export const accountService = {
  async getAccounts() {
    try {
      const { headers, userId, role } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/accounts?user_id=${userId}&role=${role}`,
        {
          method: "GET",
          credentials: "include",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch accounts");
      }

      const data = await response.json();
      return data.accounts || [];
    } catch (error) {
      console.error("Error fetching accounts:", error);
      throwMessage("error", "Failed to load accounts");
      return [];
    }
  },

  async createAccount(accountData) {
    try {
      const { headers, userId } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/accounts`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            ...accountData,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create account");
      }

      const data = await response.json();
      
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      
      throwMessage("success", "Account created successfully");
      return data;
    } catch (error) {
      console.error("Error creating account:", error);
      throwMessage("error", error.message || "Failed to create account");
      throw error;
    }
  },

  async updateAccount(accountId, updates) {
    try {
      const { headers, userId } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/accounts`,
        {
          method: "PUT",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            account_id: accountId,
            ...updates,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update account");
      }

      const data = await response.json();
      
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      
      throwMessage("success", "Account updated successfully");
      return data;
    } catch (error) {
      console.error("Error updating account:", error);
      throwMessage("error", "Failed to update account");
      throw error;
    }
  },

  async deleteAccount(accountId) {
    try {
      const { headers, userId } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/accounts`,
        {
          method: "DELETE",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            account_id: accountId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete account");
      }

      const data = await response.json();
      
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      
      throwMessage("success", "Account deleted successfully");
      return data;
    } catch (error) {
      console.error("Error deleting account:", error);
      throwMessage("error", "Failed to delete account");
      throw error;
    }
  },

  async addTransaction(accountId, transactionData) {
    try {
      const { headers, userId } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/accounts/transaction`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            account_id: accountId,
            ...transactionData,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to add transaction");
      }

      const data = await response.json();
      
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      
      throwMessage("success", "Transaction added successfully");
      return data;
    } catch (error) {
      console.error("Error adding transaction:", error);
      throwMessage("error", error.message || "Failed to add transaction");
      throw error;
    }
  },

  async deleteTransaction(accountId, transactionId) {
    try {
      const { headers, userId } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/accounts/transaction`,
        {
          method: "DELETE",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            account_id: accountId,
            transaction_id: transactionId,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete transaction");
      }

      const data = await response.json();
      
      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }
      
      throwMessage("success", "Transaction deleted successfully");
      return data;
    } catch (error) {
      console.error("Error deleting transaction:", error);
      throwMessage("error", error.message || "Failed to delete transaction");
      throw error;
    }
  },

  async updateBacktestBalance(backtestBalance) {
    try {
      const { headers, userId } = getAuthHeaders();

      const response = await fetch(
        `${process.env.REACT_APP_API}/backtest-balance`,
        {
          method: "PUT",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            backtestBalance: parseFloat(backtestBalance),
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update backtest balance");
      }

      const data = await response.json();

      if (data.user) {
        localStorage.setItem("user", JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error("Error updating backtest balance:", error);
      throwMessage("error", error.message || "Failed to update backtest balance");
      throw error;
    }
  },

  async getExpenseGroups() {
    try {
      const { headers, userId, role } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/expense-groups?user_id=${userId}&role=${role}`,
        {
          method: "GET",
          credentials: "include",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch expense groups");
      }

      const data = await response.json();
      return data.expenseGroups || [{ id: "default", name: "Expenses" }];
    } catch (error) {
      console.error("Error fetching expense groups:", error);
      return [{ id: "default", name: "Expenses" }];
    }
  },

  async updateExpenseGroups(expenseGroups) {
    try {
      const { headers, userId } = getAuthHeaders();
      
      const response = await fetch(
        `${process.env.REACT_APP_API}/expense-groups`,
        {
          method: "PUT",
          credentials: "include",
          headers,
          body: JSON.stringify({
            user_id: userId,
            expenseGroups,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update expense groups");
      }

      const data = await response.json();
      return data.expenseGroups;
    } catch (error) {
      console.error("Error updating expense groups:", error);
      throwMessage("error", "Failed to save expense groups");
      throw error;
    }
  },
};
